import type { MenuItem, MenuItemDelivery } from '@/api/types'

/**
 * The planner row's stock logic, kept out of the component so it can be tested directly.
 *
 * Everything here reads the menu-contents shape, where the incoming quantity is scoped to the
 * MENU'S day rather than to today (CHG-2026-09-menu-editor-redesign). Do not reuse these helpers
 * against the inventory page's rows — there, `incomingQty` still means today.
 */

/**
 * A SKU with no `inventory_levels` row AND nothing announced for the menu's day: the server sends
 * nulls rather than zeros, and the row says "not tracked" instead of showing four zeros, which
 * would claim the opposite.
 *
 * The incoming check is not redundant. Since the 2026-09-17 production fix an announced delivery
 * makes a SKU sellable with no levels row at all, so such a row has a real figure to show and must
 * not be dismissed as untracked — that is exactly the case that hid the kostbar READY
 * Tagesgerichte from ordering.
 */
export function isUntracked(item: MenuItem): boolean {
  return (
    item.availableQty == null &&
    item.reservedQty == null &&
    item.safetyBufferQty == null &&
    !item.incomingForDateQty &&
    !item.incomingNotCountingQty
  )
}

/** A dish, variant or not-tracked row: no stock of its own, availability from its parts. */
export function isDerivedRow(item: MenuItem): boolean {
  return item.stockKind != null && item.stockKind !== 'article'
}

export type RowChip =
  /** Sellable only because of an announced delivery for the menu's day. */
  | { kind: 'delivery'; deliveries: Array<MenuItemDelivery> }
  /** A delivery exists for the day but is switched off for pre-orders, so it is not counted. */
  | { kind: 'notCounting'; deliveries: Array<MenuItemDelivery> }
  /** Nothing sellable and nothing announced. */
  | { kind: 'soldOut' }
  | null

/**
 * At most one chip per row, in this order of precedence.
 *
 * The `delivery` chip is deliberately narrow: it appears only when the shelf alone would not
 * carry the row. A dish with plenty in stock AND a delivery coming needs no chip — the delivery
 * is not what makes it sellable, so saying so would be noise on every row after a goods receipt.
 */
export function rowChip(item: MenuItem, tracksInventory = true): RowChip {
  // A dish, variant or not-tracked article (CHG-2026-09-stock-usage-and-price-twins) has no levels
  // row by design, so the "never booked" reading below would be wrong for it. Its number is the
  // online reading of its tracked parts: sold out at 0, never while unlimited.
  if (isDerivedRow(item)) {
    return !item.unlimited && (item.sellableQty ?? 0) <= 0
      ? { kind: 'soldOut' }
      : null
  }

  // No levels row at a location that DOES track inventory is not "untracked, so unlimited": the
  // shop computes 0 for it, and the guest's add button then silently does nothing. That is a
  // sold-out row. The only reason this needs the LOCATION's flag is that paydesk has no
  // per-PRODUCT tracking flag yet — once products carry one, read it from the item instead.
  if (isUntracked(item)) return tracksInventory ? { kind: 'soldOut' } : null

  const available = item.availableQty ?? 0
  const buffer = item.safetyBufferQty ?? 0
  const incoming = item.incomingForDateQty ?? 0
  const notCounting = item.incomingNotCountingQty ?? 0
  const sellable = item.sellableQty ?? 0
  const deliveries = item.incomingDeliveries ?? []

  const counting = deliveries.filter((d) => d.countsForPreorders)
  const notCountingDocs = deliveries.filter((d) => !d.countsForPreorders)

  // The shelf alone, before anything announced, cannot carry this row.
  const shelfIsShort = available - buffer <= 0

  if (sellable > 0 && incoming > 0 && shelfIsShort) {
    return { kind: 'delivery', deliveries: counting }
  }
  if (notCounting > 0) {
    return { kind: 'notCounting', deliveries: notCountingDocs }
  }
  if (sellable === 0 && incoming === 0) {
    return { kind: 'soldOut' }
  }
  return null
}
